/**
 * Help Center Lite - Floating Help Button with AI Support and Contact Form
 * Reduces friction and prevents lost conversions
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, MessageCircle, Mail, Send, ChevronRight, Bot, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import SafeMarkdown from './SafeMarkdown';
import { a11yLabel } from '../utils/a11yLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const translations = {
  en: {
    needHelp: 'Need Help?',
    helpCenter: 'Help Center',
    howCanWeHelp: 'How can we help you today?',
    askAI: 'Ask SHS-AI',
    askAIDesc: 'Get instant answers about payments, account access, and platform issues',
    contactSupport: 'Contact Support',
    contactSupportDesc: 'Send us a message and we\'ll get back to you',
    // AI Chat
    aiTitle: 'SHS Support Assistant',
    aiSubtitle: 'I can help with payments, account access, and platform issues',
    typeMessage: 'Type your question...',
    send: 'Send',
    thinking: 'Thinking...',
    aiGreeting: 'Hello! I\'m the SHS Support Assistant. How can I help you today? I can assist with:\n\n• Payment issues\n• Account access\n• Platform questions\n• Technical problems',
    // Contact Form
    contactTitle: 'Contact Support',
    contactSubtitle: 'We typically respond within 24 hours',
    name: 'Name',
    email: 'Email',
    issue: 'Describe your issue',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'your@email.com',
    issuePlaceholder: 'Please describe the issue you\'re experiencing...',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank you!',
    messageSent: 'Your message has been sent. We\'ll get back to you within 24 hours.',
    back: 'Back',
    close: 'Close',
    // Errors
    fillAllFields: 'Please fill in all fields',
    errorSending: 'Error sending message. Please try again.'
  },
  es: {
    needHelp: '¿Necesitas ayuda?',
    helpCenter: 'Centro de Ayuda',
    howCanWeHelp: '¿Cómo podemos ayudarte hoy?',
    askAI: 'Pregunta a SHS-AI',
    askAIDesc: 'Obtén respuestas instantáneas sobre pagos, acceso a cuenta y problemas de la plataforma',
    contactSupport: 'Contactar Soporte',
    contactSupportDesc: 'Envíanos un mensaje y te responderemos',
    // AI Chat
    aiTitle: 'Asistente de Soporte SHS',
    aiSubtitle: 'Puedo ayudarte con pagos, acceso a cuenta y problemas de la plataforma',
    typeMessage: 'Escribe tu pregunta...',
    send: 'Enviar',
    thinking: 'Pensando...',
    aiGreeting: '¡Hola! Soy el Asistente de Soporte SHS. ¿Cómo puedo ayudarte hoy? Puedo asistirte con:\n\n• Problemas de pago\n• Acceso a cuenta\n• Preguntas de la plataforma\n• Problemas técnicos',
    // Contact Form
    contactTitle: 'Contactar Soporte',
    contactSubtitle: 'Normalmente respondemos dentro de 24 horas',
    name: 'Nombre',
    email: 'Correo',
    issue: 'Describe tu problema',
    namePlaceholder: 'Tu nombre',
    emailPlaceholder: 'tu@correo.com',
    issuePlaceholder: 'Por favor describe el problema que estás experimentando...',
    submit: 'Enviar',
    submitting: 'Enviando...',
    thankYou: '¡Gracias!',
    messageSent: 'Tu mensaje ha sido enviado. Te responderemos dentro de 24 horas.',
    back: 'Volver',
    close: 'Cerrar',
    // Errors
    fillAllFields: 'Por favor completa todos los campos',
    errorSending: 'Error al enviar mensaje. Por favor intenta de nuevo.'
  }
};

// Support AI System Prompt
const SUPPORT_AI_PROMPT = `You are SHS Support Assistant, a helpful support agent for Sleep Health Services. Your role is to help users with:

1. PAYMENT ISSUES:
- Explain how to make payments
- Help with payment failures
- Explain pricing and plans ($2 Explorer tier, etc.)
- Assist with refund questions (direct to support@sleephealthservices.org for refunds)

2. ACCOUNT ACCESS:
- Help with login issues
- Explain how to reset passwords
- Assist with email verification
- Help with subscription status questions

3. PLATFORM ISSUES:
- Explain how to use the Academy
- Help navigate the CPAP Fit Assistant
- Explain the Assessment tool
- Guide users to the right features

4. TECHNICAL PROBLEMS:
- Suggest common fixes (clear cache, try different browser)
- Help with audio/video issues
- Assist with mobile vs desktop questions

IMPORTANT RULES:
- Be friendly, concise, and helpful
- If you cannot resolve the issue, direct users to contact support via the form
- Do not share any internal information or make promises you can't keep
- Keep responses under 150 words
- Use bullet points when listing options
- If asked about medical advice, clarify you only provide platform support, not medical guidance`;

export const HelpCenterLite = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('menu'); // menu, ai, contact, success
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    issue: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ITER182 · Never obscure primary CTAs on the camera capture flow or the
  // admin rapid-review workspace. Shares the hide-list with AIHelper.
  // Placed AFTER all hooks so React's hook-order invariant is preserved.
  const HIDE_ON_ROUTES = ['/cpap-fit', '/admin/media-deploy'];
  const hideOnRoute = HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p));

  // Phase 1 · MobileAssistantHub bridge — open the help modal when the
  // unified mobile menu dispatches 'shs:open-help'.
  useEffect(() => {
    const openFromHub = () => {
      if (HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p))) return;
      setIsOpen(true);
      setView('menu');
    };
    window.addEventListener('shs:open-help', openFromHub);
    return () => window.removeEventListener('shs:open-help', openFromHub);
  }, [location.pathname]);
  
  // Initialize AI chat with greeting
  const initializeAIChat = () => {
    setMessages([{
      role: 'assistant',
      content: t.aiGreeting
    }]);
    setView('ai');
  };
  
  // Send message to AI
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          language: language,
          system_prompt: SUPPORT_AI_PROMPT,
          context: 'support_chat'
        })
      });
      
      if (!response.ok) throw new Error('AI request failed');
      
      const data = await response.json();
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response || data.message || 'I apologize, but I encountered an issue. Please try again or contact support.'
      }]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: language === 'es' 
          ? 'Lo siento, encontré un problema. Por favor contacta soporte directamente usando el formulario de contacto.'
          : 'I apologize, I encountered an issue. Please contact support directly using the contact form.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Submit contact form
  const submitContactForm = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.issue) {
      toast.error(t.fillAllFields);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/enterprise/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_name: 'Support Request',
          industry: 'Support',
          employee_count: 'N/A',
          contact_name: contactForm.name,
          email: contactForm.email,
          phone: '',
          message: `[SUPPORT REQUEST]\n\n${contactForm.issue}`,
          language: language
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit');
      
      setView('success');
      setContactForm({ name: '', email: '', issue: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t.errorSending);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle key press in chat
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Reset to menu
  const goBack = () => {
    setView('menu');
    setMessages([]);
    setInputMessage('');
  };
  
  // ITER182 · After all hooks are called (rules-of-hooks safe), hide entirely
  // on the camera capture and admin rapid-review workspaces.
  if (hideOnRoute) return null;

  return (
    <>
      {/* Floating Help Button — hidden on mobile (unified into MobileAssistantHub); pill on ≥sm */}
      <button
        onClick={() => setIsOpen(true)}
        className="hidden sm:flex fixed bottom-20 right-6 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 items-center justify-center group px-4 py-3 gap-2"
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="help-center-btn"
        aria-label={t.needHelp}
        title={t.needHelp}
      >
        <HelpCircle className="w-5 h-5" />
        <span className="font-medium">{t.needHelp}</span>
      </button>
      
      {/* Help Center Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl max-h-[80vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <CardHeader className="border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {view !== 'menu' && view !== 'success' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={goBack}
                      className="text-white hover:bg-white/20 -ml-2"
                     aria-label={a11yLabel("Back")}>
                      <ChevronRight className="w-4 h-4 rotate-180" />
                    </Button>
                  )}
                  <div>
                    <CardTitle className="text-lg text-white">
                      {view === 'ai' ? t.aiTitle : view === 'contact' ? t.contactTitle : t.helpCenter}
                    </CardTitle>
                    <CardDescription className="text-indigo-100 text-sm">
                      {view === 'ai' ? t.aiSubtitle : view === 'contact' ? t.contactSubtitle : t.howCanWeHelp}
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setIsOpen(false); setView('menu'); }}
                  className="text-white hover:bg-white/20"
                  aria-label={a11yLabel("X")}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-hidden p-0">
              {/* Menu View */}
              {view === 'menu' && (
                <div className="p-4 space-y-3">
                  {/* Ask AI Option */}
                  <button
                    onClick={initializeAIChat}
                    className="w-full p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left flex items-start gap-4 group"
                    data-testid="help-ask-ai-btn"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{t.askAI}</span>
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {language === 'es' ? 'Instantáneo' : 'Instant'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{t.askAIDesc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors mt-2" />
                  </button>
                  
                  {/* Contact Support Option */}
                  <button
                    onClick={() => setView('contact')}
                    className="w-full p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left flex items-start gap-4 group"
                    data-testid="help-contact-btn"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900">{t.contactSupport}</span>
                      <p className="text-sm text-gray-600 mt-1">{t.contactSupportDesc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors mt-2" />
                  </button>
                </div>
              )}
              
              {/* AI Chat View */}
              {view === 'ai' && (
                <div className="flex flex-col h-[400px]">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] rounded-lg px-4 py-2 ${
                          msg.role === 'user' 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {msg.role === 'assistant' ? (
                            <SafeMarkdown className="text-sm">{msg.content}</SafeMarkdown>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg px-4 py-2 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                          <span className="text-sm text-gray-600">{t.thinking}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Input */}
                  <div className="border-t p-4">
                    <div className="flex gap-2">
                      <Input
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={t.typeMessage}
                        disabled={isLoading}
                        className="flex-1"
                        data-testid="help-ai-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!inputMessage.trim() || isLoading}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="help-ai-send-btn"
                       aria-label={a11yLabel("Send")}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Contact Form View */}
              {view === 'contact' && (
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.name}</label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t.namePlaceholder}
                      data-testid="help-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.email}</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t.emailPlaceholder}
                      data-testid="help-contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.issue}</label>
                    <Textarea
                      value={contactForm.issue}
                      onChange={(e) => setContactForm(prev => ({ ...prev, issue: e.target.value }))}
                      placeholder={t.issuePlaceholder}
                      rows={4}
                      data-testid="help-contact-issue"
                    />
                  </div>
                  <Button
                    onClick={submitContactForm}
                    disabled={isSubmitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid="help-contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t.submitting}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t.submit}
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Success View */}
              {view === 'success' && (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.thankYou}</h3>
                  <p className="text-gray-600 mb-6">{t.messageSent}</p>
                  <Button
                    onClick={() => { setIsOpen(false); setView('menu'); }}
                    variant="outline"
                  >
                    {t.close}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default HelpCenterLite;
